/**
 * CommentItem molecule — single comment entry.
 *
 * Renders: author Avatar (sm) + name (body-sm semibold) + timestamp (caption) + body (body-sm).
 * No hardcoded strings, no gradients, no emojis.
 */

import React from 'react';
import { Avatar } from '@/components/ui/atoms/Avatar';
import { Typography } from '@/components/ui/atoms/Typography';
import type { TalentComment } from '@/types/talent.types';

export interface CommentItemProps {
  comment: TalentComment;
}

function formatTimestamp(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const CommentItem: React.FC<CommentItemProps> = ({ comment }) => {
  const initials = comment.authorName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex gap-3 py-3">
      <Avatar
        initials={initials}
        src={comment.authorAvatarUrl}
        alt={comment.authorName}
        size="sm"
        variant="neutral"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Typography variant="body-sm" weight="semibold" color="primary">
            {comment.authorName}
          </Typography>
          <Typography variant="caption" color="secondary">
            {formatTimestamp(comment.createdAt)}
          </Typography>
        </div>
        <Typography variant="body-sm" color="secondary" className="mt-1 break-words">
          {comment.body}
        </Typography>
      </div>
    </div>
  );
};

CommentItem.displayName = 'CommentItem';
